import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';

import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import {
  provideSkTour,
  provideSkWhatsNew,
  provideStarkUI,
  SK_ICON_REGISTRY,
  SK_TOUR_HOOKS,
} from '@mercadoni/stark-ui';
import {
  Activity,
  Archive,
  ArrowDown,
  ArrowDownNarrowWide,
  ArrowUp,
  ArrowUpNarrowWide,
  Bell,
  Bookmark,
  BookOpen,
  Box,
  Bug,
  Building,
  Calendar,
  ChartBar,
  ChartLine,
  ChartPie,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CirclePlay,
  CircleX,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Compass,
  Copy,
  Cpu,
  CreditCard,
  Database,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileCode,
  FileSpreadsheet,
  FileText,
  Filter,
  Flag,
  Folder,
  FolderOpen,
  GitBranch,
  GitCompare,
  Globe,
  GripVertical,
  HardDrive,
  Headphones,
  Headset,
  Heart,
  History,
  House,
  Info,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Link,
  List,
  Lock,
  LogOut,
  LUCIDE_ICONS,
  LucideIconProvider,
  Mail,
  Map,
  MapPin,
  Menu,
  MessageCircle,
  MessageSquare,
  Minus,
  Monitor,
  Network,
  Package,
  PanelLeft,
  Pencil,
  PencilLine,
  Phone,
  Pin,
  Plug,
  Plus,
  Power,
  PowerOff,
  Presentation,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  Settings,
  Settings2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  SquareCheck,
  SquareCode,
  Star,
  Store,
  Tag,
  Terminal,
  Timer,
  Trash,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Truck,
  Upload,
  User,
  UserCheck,
  UserPlus,
  Users,
  UserX,
  Wallet,
  WandSparkles,
  Webhook,
  Wifi,
  Wrench,
  X,
  Zap,
} from 'lucide-angular';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { leapBotNetworkCaptureInterceptor } from './core/interceptors/leapbot-network-capture.interceptor';
import { nebulaClientIdInterceptor } from './core/interceptors/nebula-client-id.interceptor';
import { notificationsInterceptor } from './core/interceptors/notifications.interceptor';
import { requestLogInterceptor } from './core/interceptors/request-log.interceptor';
import { getFirebaseProviders } from './core/providers/firebase.provider';
import { AuthService, ClientContextService, ClusterService } from './core/services';
import { StoreContextService } from './core/services/context/store-context.service';
import { provideNfContext } from './core/services/context/toolbelt-context.service';
import { provideFuryI18n } from './core/services/i18n/i18n.providers';
import { GlobalErrorHandler } from './core/services/ui/global-error-handler.service';
import {
  FirestoreWhatsNewStorage,
  FuryTourHooks,
  isWhatsNewToolingEnabled,
  resolveWhatsNewLabel,
  tourLabels,
  WHATS_NEW_STRINGS,
  WhatsNewDevService,
} from './onboarding';
import { FolderBookmark } from './shared/icons/folder-bookmark.icon';
import { provideNfDatasources } from './shared/services/nf-datasource.service';
import {
  provideToolbeltServices,
  TOOLBELT_DATASOURCES,
} from './shared/services/toolbelt-datasources';
import { chunkLoadNavigationErrorHandler } from './shared/utils/chunk-load-recovery.util';

const FURY_ICONS = {
  Activity,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  FileSpreadsheet,
  FolderBookmark,
  HardDrive,
  Pin,
  Presentation,
  TriangleAlert,
  Archive,
  ChartBar,
  ChartLine,
  ChartPie,
  Bell,
  BookOpen,
  Bookmark,
  Box,
  Bug,
  Building,
  Calendar,
  CircleCheck,
  Clipboard,
  Clock,
  Cloud,
  Code,
  SquareCode,
  Compass,
  Copy,
  CreditCard,
  Cpu,
  Database,
  Download,
  Pencil,
  PencilLine,
  ExternalLink,
  FileCode,
  FileText,
  Filter,
  Flag,
  FolderOpen,
  GitBranch,
  GitCompare,
  Globe,
  Headphones,
  Headset,
  LayoutGrid,
  Heart,
  History,
  House,
  Info,
  Key,
  Layers,
  LayoutDashboard,
  Link,
  List,
  Lock,
  LogOut,
  Mail,
  Map,
  MapPin,
  MessageCircle,
  MessageSquare,
  Menu,
  Minus,
  Monitor,
  Network,
  Package,
  PanelLeft,
  Phone,
  Plug,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  Settings,
  Settings2,
  Shield,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  ArrowUpNarrowWide,
  ArrowDownNarrowWide,
  Star,
  Store,
  Tag,
  Terminal,
  Timer,
  Wrench,
  TrendingDown,
  TrendingUp,
  Truck,
  Upload,
  User,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  Wallet,
  WandSparkles,
  Webhook,
  Wifi,
  X,
  CircleX,
  Zap,
  Folder,
  File,
  SquareCheck,
  CirclePlay,
  Eye,
  EyeOff,
  Trash,
  Trash2,
  GripVertical,
  Power,
  PowerOff,
};

/**
 * sk-icon (stark-ui) has its own built-in icon set, separate from FURY_ICONS/lucide-angular
 * above (which feeds nf-icon). Names not in stark-ui's built-in list render blank — register
 * the ones this app uses that aren't covered, matching stark-ui's stroke style.
 */
const SK_ICONS = new globalThis.Map<string, string>([
  [
    'wrench',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z"/></svg>',
  ],
  [
    'layers',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"/><path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"/><path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"/></svg>',
  ],
  [
    'shield-check',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
  ],
  [
    'webhook',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"/><path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06"/><path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"/></svg>',
  ],
  [
    'save',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/><path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/><path d="M7 3v4a1 1 0 0 0 1 1h7"/></svg>',
  ],
  [
    'sun',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>',
  ],
  [
    'moon',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"/></svg>',
  ],
  [
    'sparkles',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg>',
  ],
  [
    'arrow-left',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>',
  ],
  [
    'home',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>',
  ],
  [
    'user',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
  ],
  [
    'log-out',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/></svg>',
  ],
  [
    'circle',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>',
  ],
  [
    'message-square',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  ],
  [
    'square',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="5" y="5" rx="2"/></svg>',
  ],
  [
    'bot',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="8" width="16" height="12" rx="3"/><path d="M12 8V4M9 13v1M15 13v1M2 14v2M22 14v2"/></svg>',
  ],
  [
    'book-open',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2zM22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z"/></svg>',
  ],
  [
    'ticket-check',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 5h14v4a2 2 0 0 0 0 4v6H5v-6a2 2 0 0 0 0-4z"/><path d="m9 12 2 2 4-4"/></svg>',
  ],
  [
    'lock',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
  ],
  [
    'triangle-alert',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 2 20h20zM12 10v4M12 17h.01"/></svg>',
  ],
  [
    'check',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
  ],
  [
    'paperclip',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 11-8.5 8.5a5 5 0 0 1-7-7L14 4a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4L15 7"/></svg>',
  ],
  [
    'video',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="13" height="12" rx="2"/><path d="m16 10 5-3v10l-5-3"/></svg>',
  ],
  [
    'volume-2',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>',
  ],
  [
    'volume-x',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="m22 9-6 6"/><path d="m16 9 6 6"/></svg>',
  ],
  [
    'building-2',
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>',
  ],
]);

/** True only for instaleap.io itself or one of its subdomains — not a substring match. */
function isInstaleapHost(uri: string): boolean {
  try {
    const { hostname } = new URL(uri, window.location.origin);
    return hostname === 'instaleap.io' || hostname.endsWith('.instaleap.io');
  } catch {
    return false;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Localization: catalogs of the resolved language are loaded before the shell renders.
    provideFuryI18n(),
    // Unhandled errors and rejections: log with a reference and show one throttled toast.
    { provide: ErrorHandler, useExisting: GlobalErrorHandler },
    provideAppInitializer(() => {
      inject(GlobalErrorHandler).listenToRejections(window);
    }),
    // After a deploy, open tabs still reference the previous build's lazy chunks; reload once instead
    // of failing with "Failed to fetch dynamically imported module".
    provideRouter(routes, withNavigationErrorHandler(chunkLoadNavigationErrorHandler)),
    // Default mountToastContainer:true auto-mounts the single container on document.body via
    // ApplicationRef — independent of the router, so it exists even before ShellComponent (or
    // any guarded route inside it) ever activates. ShellComponent used to ALSO place its own
    // <sk-toast-container/>, which duplicated every toast; that manual one was removed instead
    // of disabling this default, since a container scoped to Shell's own template isn't mounted
    // yet when a route guard (e.g. adminGuard) rejects navigation before Shell activates — its
    // toast would otherwise have nowhere to render.
    provideStarkUI(),
    provideSkWhatsNew({
      // No `releases` source on purpose: WhatsNewVisibilityService hands the engine only the
      // releases the current user may see (setCatalog), after roles, tenant and first-visit state
      // are known. A source here would load the unfiltered catalog and let the one-shot
      // auto-open run against it.
      storage: FirestoreWhatsNewStorage,
      // Fury applies the visibility window itself (release schedule / 14 days, welcome exempt),
      // so the engine's own window is effectively disabled to avoid a second, conflicting rule.
      autoShowWindowDays: 36500,
      // <nf-whats-new-modal> is placed in ShellComponent, next to the tour overlay.
      mount: 'manual',
      // The labels are catalog keys; the engine resolves them each time a session opens.
      labels: { ...WHATS_NEW_STRINGS },
      textResolver: resolveWhatsNewLabel,
    }),
    provideAppInitializer(() => {
      // Console API (window.furyWhatsNew) for local dev and PR preview channels only.
      if (isWhatsNewToolingEnabled()) inject(WhatsNewDevService);
    }),
    provideSkTour({ labels: tourLabels }),
    { provide: SK_TOUR_HOOKS, useExisting: FuryTourHooks },
    provideToolbeltServices(),
    provideNfDatasources(TOOLBELT_DATASOURCES),
    provideNfContext({
      clientId: () => inject(ClientContextService).selectedClientId(),
      storeId: () => inject(StoreContextService).selectedStoreId(),
      userId: () => inject(AuthService).userEmail(),
      env: () => inject(ClusterService).activeCluster().clusterName,
    }),
    { provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(FURY_ICONS) },
    { provide: SK_ICON_REGISTRY, useValue: SK_ICONS },
    provideHttpClient(
      withInterceptors([
        authHttpInterceptorFn,
        nebulaClientIdInterceptor,
        notificationsInterceptor,
        requestLogInterceptor,
        leapBotNetworkCaptureInterceptor,
      ]),
    ),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      cacheLocation: 'localstorage',
      authorizationParams: {
        redirect_uri: window.location.origin,
        audience: environment.auth0.audience,
      },
      // Note: also configure Allowed Callback URLs in Auth0 Dashboard as:
      // http://localhost:4200  (for development)
      // https://your-production-domain.com  (for production)
      httpInterceptor: {
        allowedList: [
          {
            uri: environment.auth0.audience,
            tokenOptions: { authorizationParams: { audience: environment.auth0.audience } },
          },
          // LeapBot first: auth0-angular uses the first matching entry, and LeapBot's host is also under
          // instaleap.io, so this entry must come before the general one below for a LeapBot-specific
          // audience to apply to HTTP calls as well as to the answer channel.
          ...(environment.leapbot
            ? [
                {
                  uri: `${environment.leapbot.apiUrl.replace(/\/+$/, '')}/*`,
                  tokenOptions: {
                    authorizationParams: {
                      audience: environment.leapbot.audience ?? environment.auth0.audience,
                    },
                  },
                },
              ]
            : []),
          // Coulson/Sentinel/Fury clusters — all instaleap.io domains get the same Auth0 token
          {
            uriMatcher: isInstaleapHost,
            tokenOptions: { authorizationParams: { audience: environment.auth0.audience } },
          },
        ],
      },
    }),
    ...getFirebaseProviders(),
  ],
};
